import { useState, useEffect, useCallback } from 'react';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '../components/ui/resizable';
import { ScrollArea } from '../components/ui/scroll-area';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Plus, Search, Database, Trash2, Edit, Copy, GitBranch, History, 
  RotateCcw, Play, CheckCircle, XCircle, AlertCircle, Clock, 
  Upload, Download, Settings, Loader2, X, 
  Sparkles, BookOpen, Users, Shield, Link, AlertTriangle,
  Calculator, Ruler, Layers, FileCode, ChevronRight, ChevronDown,
  Eye, ExternalLink, Tag, FileText, Table, Key, RefreshCw, Save,
  LinkIcon, Columns, ArrowRight, GitFork, ScrollText
} from 'lucide-react';
import CodeMirror from '@uiw/react-codemirror';
import { sql } from '@codemirror/lang-sql';
import { markdown } from '@codemirror/lang-markdown';
import { oneDark } from '@codemirror/theme-one-dark';
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer-continued';
import { cn } from '../lib/utils';
import {
  getConcepts, createConcept, updateConcept, deleteConcept,
  getConceptVersions, getConceptVersionDiff, restoreConceptVersion,
  validateConcept, searchSimilarConcepts,
  bulkImportConcepts, bulkExportConcepts,
  getConceptMetadata, saveConceptMetadata, updateColumnMetadata,
  addColumnMetadata, deleteColumnMetadata, addLink, updateLink, deleteLink,
  getMetadataVersions, restoreMetadataVersion, deleteMetadataVersion,
  regenerateGlossary,
  getUniversalInstruction, updateUniversalInstruction,
  getUniversalInstructionVersions, getUniversalInstructionDiff, restoreUniversalInstructionVersion
} from '../lib/api';

// Status colors
const statusColors = {
  draft: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  approved: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  trusted: 'bg-green-500/20 text-green-400 border-green-500/30',
  deprecated: 'bg-red-500/20 text-red-400 border-red-500/30'
};

const statusIcons = {
  draft: Clock,
  approved: CheckCircle,
  trusted: Shield,
  deprecated: XCircle
};

// Concept type icons and colors
const typeConfig = {
  metric: { icon: Calculator, color: 'text-[#0EA5E9]', bg: 'bg-[#0EA5E9]/10' },
  dimension: { icon: Ruler, color: 'text-[#7C3AED]', bg: 'bg-[#7C3AED]/10' },
  derived: { icon: Layers, color: 'text-[#F59E0B]', bg: 'bg-[#F59E0B]/10' }
};

const sqlDialects = [
  { value: 'databricks', label: 'Databricks SQL' },
  { value: 'snowflake', label: 'Snowflake SQL' },
  { value: 'standard', label: 'Standard SQL' }
];

export default function AnalyticsCatalogue() {
  // State
  const [concepts, setConcepts] = useState([]);
  const [selectedConcept, setSelectedConcept] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterTags, setFilterTags] = useState([]);

  // Filter options
  const [allTags, setAllTags] = useState([]);
  const [allTeams, setAllTeams] = useState([]);

  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showImportExport, setShowImportExport] = useState(false);
  const [showSimilarSearch, setShowSimilarSearch] = useState(false);
  const [showUniversalInstructions, setShowUniversalInstructions] = useState(false);

  // Universal Instructions state
  const [universalInstruction, setUniversalInstruction] = useState('');
  const [universalInstructionVersion, setUniversalInstructionVersion] = useState(0);
  const [universalInstructionVersions, setUniversalInstructionVersions] = useState([]);
  const [savingInstruction, setSavingInstruction] = useState(false);
  const [showInstructionHistory, setShowInstructionHistory] = useState(false);
  const [instructionDiffData, setInstructionDiffData] = useState(null);
  const [selectedInstructionVersion, setSelectedInstructionVersion] = useState(null);
  const [loadingInstructionDiff, setLoadingInstructionDiff] = useState(false);

  // Edit mode
  const [editMode, setEditMode] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    concept_type: 'metric',
    description: '',
    definition: '',
    calculation_steps: [],
    computation_methods: [{ dialect: 'databricks', sql_template: '', description: '', is_default: true }],
    base_tables: [],
    base_columns: [],
    parameters: [],
    team: '',
    tags: [],
    aliases: [],
    status: 'draft',
    owner: '',
    reviewer: '',
    effective_from: '',
    effective_to: '',
    linked_few_shots: [],
    data_freshness_sla: '',
    known_limitations: ''
  });
  const [newTag, setNewTag] = useState('');
  const [newAlias, setNewAlias] = useState('');
  const [newTable, setNewTable] = useState('');
  const [newColumn, setNewColumn] = useState('');

  // Version history
  const [versions, setVersions] = useState([]);
  const [selectedVersionForDiff, setSelectedVersionForDiff] = useState(null);
  const [diffData, setDiffData] = useState(null);
  const [loadingDiff, setLoadingDiff] = useState(false);

  // Similar search
  const [similarQuery, setSimilarQuery] = useState('');
  const [similarResults, setSimilarResults] = useState([]);
  const [searchingSimilar, setSearchingSimilar] = useState(false);

  // Load concepts
  const loadConcepts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getConcepts({
        search: searchQuery || undefined,
        concept_type: filterType !== 'all' ? filterType : undefined,
        status: filterStatus !== 'all' ? filterStatus : undefined,
        tags: filterTags.length > 0 ? filterTags.join(',') : undefined
      });
      setConcepts(res.data.concepts || []);
      setAllTags(res.data.all_tags || []);
      setAllTeams(res.data.all_teams || []);
    } catch (err) {
      toast.error('Failed to load concepts');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, filterType, filterStatus, filterTags]);

  useEffect(() => {
    loadConcepts();
  }, [loadConcepts]);

  // Create/Update concept
  const handleSaveConcept = async () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Description is required');
      return;
    }

    try {
      const payload = {
        ...formData,
        calculation_steps: formData.calculation_steps.map((s, i) => ({
          step_number: i + 1,
          description: s.description,
          sql_template: s.sql_template,
          formula: s.formula
        })),
        computation_methods: formData.computation_methods.map(m => ({
          dialect: m.dialect,
          sql_template: m.sql_template,
          description: m.description,
          is_default: m.is_default
        }))
      };

      if (editMode && selectedConcept) {
        await updateConcept(selectedConcept.id, payload);
        toast.success('Concept updated successfully');
      } else {
        await createConcept(payload);
        toast.success('Concept created successfully');
      }
      setShowCreateDialog(false);
      resetForm();
      loadConcepts();
    } catch (err) {
      toast.error(editMode ? 'Failed to update concept' : 'Failed to create concept');
    }
  };

  // Delete concept
  const handleDeleteConcept = async (id) => {
    if (!confirm('Are you sure you want to delete this concept?')) return;
    try {
      await deleteConcept(id);
      toast.success('Concept deleted');
      if (selectedConcept?.id === id) setSelectedConcept(null);
      loadConcepts();
    } catch (err) {
      toast.error('Failed to delete concept');
    }
  };

  // Load versions
  const loadVersions = async (conceptId) => {
    try {
      const res = await getConceptVersions(conceptId);
      setVersions(res.data);
      setSelectedVersionForDiff(null);
      setDiffData(null);
    } catch (err) {
      toast.error('Failed to load versions');
    }
  };

  // Load diff for a specific version
  const loadDiff = async (conceptId, versionNum) => {
    setLoadingDiff(true);
    setSelectedVersionForDiff(versionNum);
    try {
      const res = await getConceptVersionDiff(conceptId, versionNum);
      setDiffData(res.data);
    } catch (err) {
      toast.error('Failed to load diff');
      setDiffData(null);
    } finally {
      setLoadingDiff(false);
    }
  };

  // Universal Instructions functions
  const loadUniversalInstruction = async () => {
    try {
      const res = await getUniversalInstruction();
      setUniversalInstruction(res.data.content || '');
      setUniversalInstructionVersion(res.data.version || 0);
    } catch (err) {
      toast.error('Failed to load universal instruction');
    }
  };

  const saveUniversalInstruction = async () => {
    setSavingInstruction(true);
    try {
      await updateUniversalInstruction({ content: universalInstruction });
      toast.success('Universal instruction saved');
      loadUniversalInstruction();
      loadInstructionVersions();
    } catch (err) {
      toast.error('Failed to save universal instruction');
    } finally {
      setSavingInstruction(false);
    }
  };

  const loadInstructionVersions = async () => {
    try {
      const res = await getUniversalInstructionVersions();
      setUniversalInstructionVersions(res.data || []);
    } catch (err) {
      toast.error('Failed to load instruction versions');
    }
  };

  const loadInstructionDiff = async (versionNum) => {
    setLoadingInstructionDiff(true);
    setSelectedInstructionVersion(versionNum);
    try {
      const res = await getUniversalInstructionDiff(versionNum);
      setInstructionDiffData(res.data);
    } catch (err) {
      toast.error('Failed to load diff');
      setInstructionDiffData(null);
    } finally {
      setLoadingInstructionDiff(false);
    }
  };

  const restoreInstructionVersion = async (versionNum) => {
    try {
      await restoreUniversalInstructionVersion(versionNum);
      toast.success('Version restored');
      loadUniversalInstruction();
      loadInstructionVersions();
    } catch (err) {
      toast.error('Failed to restore version');
    }
  };

  // Search similar
  const handleSearchSimilar = async () => {
    if (!similarQuery.trim()) return;
    setSearchingSimilar(true);
    try {
      const res = await searchSimilarConcepts(similarQuery);
      setSimilarResults(res.data);
    } catch (err) {
      toast.error('Failed to search');
    } finally {
      setSearchingSimilar(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      concept_type: 'metric',
      description: '',
      definition: '',
      calculation_steps: [],
      computation_methods: [{ dialect: 'databricks', sql_template: '', description: '', is_default: true }],
      base_tables: [],
      base_columns: [],
      parameters: [],
      team: '',
      tags: [],
      aliases: [],
      status: 'draft',
      owner: '',
      reviewer: '',
      effective_from: '',
      effective_to: '',
      linked_few_shots: [],
      data_freshness_sla: '',
      known_limitations: ''
    });
    setEditMode(false);
    setNewTag('');
    setNewAlias('');
  };

  // Open edit dialog
  const openEditDialog = (concept) => {
    setFormData({
      name: concept.name,
      concept_type: concept.concept_type,
      description: concept.description || '',
      definition: concept.definition || '',
      calculation_steps: concept.calculation_steps || [],
      computation_methods: concept.computation_methods || [{ dialect: 'databricks', sql_template: '', description: '', is_default: true }],
      base_tables: concept.base_tables || [],
      base_columns: concept.base_columns || [],
      parameters: concept.parameters || [],
      team: concept.team || '',
      tags: concept.tags || [],
      aliases: concept.aliases || [],
      status: concept.status || 'draft',
      owner: concept.owner || '',
      reviewer: concept.reviewer || '',
      effective_from: concept.effective_from || '',
      effective_to: concept.effective_to || '',
      linked_few_shots: concept.linked_few_shots || [],
      data_freshness_sla: concept.data_freshness_sla || '',
      known_limitations: concept.known_limitations || ''
    });
    setEditMode(true);
    setShowCreateDialog(true);
  };

  // Add/remove helpers
  const addComputationMethod = () => {
    setFormData(prev => ({
      ...prev,
      computation_methods: [...prev.computation_methods, { dialect: 'snowflake', sql_template: '', description: '', is_default: false }]
    }));
  };

  const removeComputationMethod = (index) => {
    if (formData.computation_methods.length <= 1) return;
    setFormData(prev => ({
      ...prev,
      computation_methods: prev.computation_methods.filter((_, i) => i !== index)
    }));
  };

  const updateComputationMethod = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      computation_methods: prev.computation_methods.map((m, i) => 
        i === index ? { ...m, [field]: value } : m
      )
    }));
  };

  const addCalculationStep = () => {
    setFormData(prev => ({
      ...prev,
      calculation_steps: [...prev.calculation_steps, { description: '', sql_template: '', formula: '' }]
    }));
  };

  const removeCalculationStep = (index) => {
    setFormData(prev => ({
      ...prev,
      calculation_steps: prev.calculation_steps.filter((_, i) => i !== index)
    }));
  };

  const updateCalculationStep = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      calculation_steps: prev.calculation_steps.map((s, i) => 
        i === index ? { ...s, [field]: value } : s
      )
    }));
  };
  
  return (
    <div className="h-full flex flex-col bg-[#030712]" data-testid="analytics-catalogue-page">
      {/* Header */}
      <div className="h-12 border-b border-[#1E293B] flex items-center justify-between px-4 bg-[#0F172A]">
        <div className="flex items-center gap-4">
          <h2 className="text-sm font-medium text-[#F1F5F9]">Analytics Catalogue</h2>
          <Badge variant="outline" className="text-xs">
            {concepts.length} concepts
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { loadUniversalInstruction(); setShowUniversalInstructions(true); }}
            className="text-[#64748B] hover:text-white"
            data-testid="universal-instructions-btn"
          >
            <ScrollText className="w-4 h-4 mr-1" />
            Universal Instructions
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setShowSimilarSearch(true); }}
            className="text-[#64748B] hover:text-white"
          >
            <Sparkles className="w-4 h-4 mr-1" />
            Find Similar
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowImportExport(true)}
            className="text-[#64748B] hover:text-white"
          >
            <Upload className="w-4 h-4 mr-1" />
            Import/Export
          </Button>
          <Button
            size="sm"
            onClick={() => { resetForm(); setShowCreateDialog(true); }}
            className="bg-[#0EA5E9] hover:bg-[#0284C7]"
            data-testid="create-concept-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            New Concept
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden">
        <ResizablePanelGroup direction="horizontal">
          {/* Left Panel - List & Filters */}
          <ResizablePanel defaultSize={35} minSize={25} maxSize={50}>
            <div className="h-full flex flex-col bg-[#0F172A] border-r border-[#1E293B]">
              {/* Search & Filters */}
              <div className="p-3 border-b border-[#1E293B] space-y-3">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
                  <Input
                    placeholder="Search concepts..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 bg-[#030712] border-[#1E293B]"
                  />
                </div>
                <div className="flex gap-2">
                  <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger className="flex-1 h-8 text-xs bg-[#030712] border-[#1E293B]">
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="metric">Metrics</SelectItem>
                      <SelectItem value="dimension">Dimensions</SelectItem>
                      <SelectItem value="derived">Derived</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="flex-1 h-8 text-xs bg-[#030712] border-[#1E293B]">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="trusted">Trusted</SelectItem>
                      <SelectItem value="deprecated">Deprecated</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {allTags.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {allTags.slice(0, 8).map(tag => (
                      <Badge
                        key={tag}
                        variant="outline"
                        className={cn(
                          "text-[10px] cursor-pointer",
                          filterTags.includes(tag) 
                            ? "bg-[#0EA5E9]/20 text-[#0EA5E9]" 
                            : "text-[#64748B] hover:text-white"
                        )}
                        onClick={() => setFilterTags(prev => 
                          prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
                        )}
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Concept List */}
              <ScrollArea className="flex-1">
                <div className="p-2 space-y-1">
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-[#0EA5E9]" />
                    </div>
                  ) : concepts.length === 0 ? (
                    <div className="text-center py-8 text-[#64748B]">
                      <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No concepts found</p>
                      <p className="text-xs mt-1">Create your first concept</p>
                    </div>
                  ) : (
                    concepts.map(concept => {
                      const TypeIcon = typeConfig[concept.concept_type]?.icon || Calculator;
                      const StatusIcon = statusIcons[concept.status] || Clock;
                      return (
                        <div
                          key={concept.id}
                          className={cn(
                            "p-3 rounded-md cursor-pointer transition-all border",
                            selectedConcept?.id === concept.id
                              ? "bg-[#0EA5E9]/10 border-[#0EA5E9]/30"
                              : "bg-[#030712] border-[#1E293B] hover:border-[#334155]"
                          )}
                          onClick={() => setSelectedConcept(concept)}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <div className={cn("p-1 rounded", typeConfig[concept.concept_type]?.bg)}>
                                <TypeIcon className={cn("w-3 h-3", typeConfig[concept.concept_type]?.color)} />
                              </div>
                              <span className="text-sm text-[#F1F5F9] truncate">{concept.name}</span>
                            </div>
                            <Badge variant="outline" className={cn("text-[10px] shrink-0", statusColors[concept.status])}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {concept.status}
                            </Badge>
                          </div>
                          <p className="text-xs text-[#64748B] line-clamp-2 mt-2">{concept.description}</p>
                          <div className="mt-2 flex items-center gap-2 flex-wrap">
                            {concept.domain && (
                              <Badge variant="outline" className="text-[10px] text-[#7C3AED]">{concept.domain}</Badge>
                            )}
                            {concept.tags?.slice(0, 2).map(tag => (
                              <Badge key={tag} variant="outline" className="text-[10px] text-[#64748B]">{tag}</Badge>
                            ))}
                          </div>
                          <div className="mt-2 flex items-center gap-3 text-[10px] text-[#64748B]">
                            <span className="flex items-center gap-1">
                              <GitBranch className="w-3 h-3" />
                              v{concept.version || 1}
                            </span>
                            {concept.usage_count > 0 && (
                              <span className="flex items-center gap-1">
                                <Eye className="w-3 h-3" />
                                {concept.usage_count} uses
                              </span>
                            )}
                            {concept.owner && (
                              <span className="flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                {concept.owner}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </ScrollArea>
            </div>
          </ResizablePanel>

          <ResizableHandle />

          {/* Right Panel - Detail View */}
          <ResizablePanel defaultSize={65} minSize={50}>
            {selectedConcept ? (
              <ConceptDetailView
                concept={selectedConcept}
                onEdit={() => openEditDialog(selectedConcept)}
                onDelete={() => handleDeleteConcept(selectedConcept.id)}
                onViewHistory={() => { loadVersions(selectedConcept.id); setShowVersionHistory(true); }}
                onDuplicate={() => {
                  openEditDialog({ ...selectedConcept, name: selectedConcept.name + ' (Copy)', status: 'draft' });
                  setEditMode(false);
                }}
              />
            ) : (
              <div className="h-full flex items-center justify-center bg-[#030712]">
                <div className="text-center text-[#64748B]">
                  <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p className="text-sm">Select a concept to view details</p>
                  <p className="text-xs mt-1">or create a new one</p>
                </div>
              </div>
            )}
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-[#0F172A] border-[#1E293B]">
          <DialogHeader>
            <DialogTitle className="text-[#F1F5F9]">
              {editMode ? 'Edit Concept' : 'Create New Concept'}
            </DialogTitle>
            <DialogDescription className="text-[#64748B]">
              Define a metric, dimension, or derived concept with its calculation logic
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="definition" className="mt-4">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="definition">Definition</TabsTrigger>
              <TabsTrigger value="calculation">Calculation</TabsTrigger>
              <TabsTrigger value="sources">Sources</TabsTrigger>
              <TabsTrigger value="metadata">Metadata</TabsTrigger>
              <TabsTrigger value="governance">Governance</TabsTrigger>
            </TabsList>

            {/* Definition Tab */}
            <TabsContent value="definition" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Monthly Recurring Revenue"
                    className="bg-[#030712] border-[#1E293B]"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Type *</Label>
                  <Select value={formData.concept_type} onValueChange={(v) => setFormData(prev => ({ ...prev, concept_type: v }))}>
                    <SelectTrigger className="bg-[#030712] border-[#1E293B]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="metric">Metric</SelectItem>
                      <SelectItem value="dimension">Dimension</SelectItem>
                      <SelectItem value="derived">Derived</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of what this concept represents..."
                  className="bg-[#030712] border-[#1E293B] min-h-[80px]"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#0EA5E9]" />
                    Definition (Rich Text)
                  </Label>
                  <span className="text-[10px] text-[#64748B]">Supports Markdown formatting</span>
                </div>
                <div className="text-[10px] text-[#64748B] mb-2">
                  Include business meaning, assumptions, caveats, and any other context. Use ## for headers, ** for bold, - for lists.
                </div>
                <CodeMirror
                  value={formData.definition}
                  height="250px"
                  theme={oneDark}
                  extensions={[markdown()]}
                  onChange={(value) => setFormData(prev => ({ ...prev, definition: value }))}
                  placeholder={`## Business Meaning
Describe the business context and purpose...

## Assumptions
- Assumption 1
- Assumption 2

## Caveats
- Known limitation 1
- Known limitation 2`}
                  className="rounded-md overflow-hidden border border-[#1E293B]"
                />
              </div>

              <div className="space-y-2">
                <Label>Aliases (Alternative Names)</Label>
                <div className="flex gap-2">
                  <Input
                    value={newAlias}
                    onChange={(e) => setNewAlias(e.target.value)}
                    placeholder="Add alias..."
                    className="bg-[#030712] border-[#1E293B]"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && newAlias.trim()) {
                        e.preventDefault();
                        setFormData(prev => ({ ...prev, aliases: [...prev.aliases, newAlias.trim()] }));
                        setNewAlias('');
                      }
                    }}
                  />
                  <Button variant="outline" onClick={() => {
                    if (newAlias.trim()) {
                      setFormData(prev => ({ ...prev, aliases: [...prev.aliases, newAlias.trim()] }));
                      setNewAlias('');
                    }
                  }}><Plus className="w-4 h-4" /></Button>
                </div>
                {formData.aliases.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.aliases.map((alias, i) => (
                      <Badge key={i} variant="outline" className="cursor-pointer hover:bg-red-500/20" onClick={() => 
                        setFormData(prev => ({ ...prev, aliases: prev.aliases.filter((_, idx) => idx !== i) }))
                      }>
                        {alias} <X className="w-3 h-3 ml-1" />
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Calculation Tab */}
            <TabsContent value="calculation" className="space-y-4 mt-4">
              {/* Calculation Steps */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Calculation Steps</Label>
                  <Button size="sm" variant="outline" onClick={addCalculationStep}>
                    <Plus className="w-4 h-4 mr-1" /> Add Step
                  </Button>
                </div>
                {formData.calculation_steps.map((step, index) => (
                  <div key={index} className="p-3 bg-[#030712] rounded-md border border-[#1E293B] space-y-2">
                    <div className="flex items-center justify-between">
                      <Badge variant="outline">Step {index + 1}</Badge>
                      <Button size="sm" variant="ghost" onClick={() => removeCalculationStep(index)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <Input
                      value={step.description}
                      onChange={(e) => updateCalculationStep(index, 'description', e.target.value)}
                      placeholder="Step description..."
                      className="bg-[#0F172A] border-[#1E293B]"
                    />
                    <Input
                      value={step.formula || ''}
                      onChange={(e) => updateCalculationStep(index, 'formula', e.target.value)}
                      placeholder="Formula (e.g., SUM(revenue) / COUNT(DISTINCT customer_id))"
                      className="bg-[#0F172A] border-[#1E293B] font-mono text-sm"
                    />
                  </div>
                ))}
              </div>

              {/* Computation Methods (SQL) */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>SQL Templates</Label>
                  <Button size="sm" variant="outline" onClick={addComputationMethod}>
                    <Plus className="w-4 h-4 mr-1" /> Add Dialect
                  </Button>
                </div>
                {formData.computation_methods.map((method, index) => (
                  <div key={index} className="p-3 bg-[#030712] rounded-md border border-[#1E293B] space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Select value={method.dialect} onValueChange={(v) => updateComputationMethod(index, 'dialect', v)}>
                          <SelectTrigger className="w-[160px] h-8 text-xs bg-[#0F172A] border-[#1E293B]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {sqlDialects.map(d => (
                              <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {method.is_default && <Badge className="bg-[#0EA5E9]/20 text-[#0EA5E9] text-[10px]">Default</Badge>}
                      </div>
                      {formData.computation_methods.length > 1 && (
                        <Button size="sm" variant="ghost" onClick={() => removeComputationMethod(index)}>
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <CodeMirror
                      value={method.sql_template}
                      height="150px"
                      theme={oneDark}
                      extensions={[sql()]}
                      onChange={(value) => updateComputationMethod(index, 'sql_template', value)}
                      className="rounded-md overflow-hidden border border-[#1E293B]"
                    />
                    <Input
                      value={method.description || ''}
                      onChange={(e) => updateComputationMethod(index, 'description', e.target.value)}
                      placeholder="Description of this implementation..."
                      className="bg-[#0F172A] border-[#1E293B] text-sm"
                    />
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* Sources Tab */}
            <TabsContent value="sources" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Base Tables</Label>
                <div className="flex gap-2">
                  <Input
                    value={newTable}
                    onChange={(e) => setNewTable(e.target.value)}
                    placeholder="Add table name..."
                    className="bg-[#030712] border-[#1E293B]"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && newTable.trim()) {
                        e.preventDefault();
                        setFormData(prev => ({ ...prev, base_tables: [...prev.base_tables, newTable.trim()] }));
                        setNewTable('');
                      }
                    }}
                  />
                  <Button variant="outline" onClick={() => {
                    if (newTable.trim()) {
                      setFormData(prev => ({ ...prev, base_tables: [...prev.base_tables, newTable.trim()] }));
                      setNewTable('');
                    }
                  }}><Plus className="w-4 h-4" /></Button>
                </div>
                {formData.base_tables.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.base_tables.map((table, i) => (
                      <Badge key={i} variant="outline" className="cursor-pointer hover:bg-red-500/20 font-mono" onClick={() => 
                        setFormData(prev => ({ ...prev, base_tables: prev.base_tables.filter((_, idx) => idx !== i) }))
                      }>
                        <Database className="w-3 h-3 mr-1" /> {table} <X className="w-3 h-3 ml-1" />
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Base Columns</Label>
                <div className="flex gap-2">
                  <Input
                    value={newColumn}
                    onChange={(e) => setNewColumn(e.target.value)}
                    placeholder="Add column name (table.column)..."
                    className="bg-[#030712] border-[#1E293B]"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && newColumn.trim()) {
                        e.preventDefault();
                        setFormData(prev => ({ ...prev, base_columns: [...prev.base_columns, newColumn.trim()] }));
                        setNewColumn('');
                      }
                    }}
                  />
                  <Button variant="outline" onClick={() => {
                    if (newColumn.trim()) {
                      setFormData(prev => ({ ...prev, base_columns: [...prev.base_columns, newColumn.trim()] }));
                      setNewColumn('');
                    }
                  }}><Plus className="w-4 h-4" /></Button>
                </div>
                {formData.base_columns.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.base_columns.map((col, i) => (
                      <Badge key={i} variant="outline" className="cursor-pointer hover:bg-red-500/20 font-mono text-xs" onClick={() => 
                        setFormData(prev => ({ ...prev, base_columns: prev.base_columns.filter((_, idx) => idx !== i) }))
                      }>
                        {col} <X className="w-3 h-3 ml-1" />
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div className="p-4 bg-[#030712] rounded-md border border-[#1E293B]">
                <Label className="text-[#64748B] text-xs">Parameters (Date ranges, Filters, etc.)</Label>
                <p className="text-xs text-[#64748B] mt-2">
                  Coming soon: Define parameterized inputs like date ranges, granularity, and filter conditions.
                </p>
              </div>
            </TabsContent>

            {/* Metadata Tab */}
            <TabsContent value="metadata" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Domain</Label>
                  <Input
                    value={formData.domain}
                    onChange={(e) => setFormData(prev => ({ ...prev, domain: e.target.value }))}
                    placeholder="e.g., Sales, Marketing, Finance"
                    className="bg-[#030712] border-[#1E293B]"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Team</Label>
                  <Input
                    value={formData.team}
                    onChange={(e) => setFormData(prev => ({ ...prev, team: e.target.value }))}
                    placeholder="e.g., Data Platform, Analytics"
                    className="bg-[#030712] border-[#1E293B]"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Tags</Label>
                <div className="flex gap-2">
                  <Input
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="Add tag..."
                    className="bg-[#030712] border-[#1E293B]"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && newTag.trim()) {
                        e.preventDefault();
                        setFormData(prev => ({ ...prev, tags: [...prev.tags, newTag.trim()] }));
                        setNewTag('');
                      }
                    }}
                  />
                  <Button variant="outline" onClick={() => {
                    if (newTag.trim()) {
                      setFormData(prev => ({ ...prev, tags: [...prev.tags, newTag.trim()] }));
                      setNewTag('');
                    }
                  }}><Plus className="w-4 h-4" /></Button>
                </div>
                {formData.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.tags.map((tag, i) => (
                      <Badge key={i} variant="outline" className="cursor-pointer hover:bg-red-500/20" onClick={() => 
                        setFormData(prev => ({ ...prev, tags: prev.tags.filter((_, idx) => idx !== i) }))
                      }>
                        <Tag className="w-3 h-3 mr-1" /> {tag} <X className="w-3 h-3 ml-1" />
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Data Freshness SLA</Label>
                  <Input
                    value={formData.data_freshness_sla}
                    onChange={(e) => setFormData(prev => ({ ...prev, data_freshness_sla: e.target.value }))}
                    placeholder="e.g., Daily by 6 AM UTC"
                    className="bg-[#030712] border-[#1E293B]"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData(prev => ({ ...prev, status: v }))}>
                    <SelectTrigger className="bg-[#030712] border-[#1E293B]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="trusted">Trusted</SelectItem>
                      <SelectItem value="deprecated">Deprecated</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Known Limitations</Label>
                <Textarea
                  value={formData.known_limitations}
                  onChange={(e) => setFormData(prev => ({ ...prev, known_limitations: e.target.value }))}
                  placeholder="Document known accuracy issues, edge cases, etc."
                  className="bg-[#030712] border-[#1E293B]"
                />
              </div>
            </TabsContent>

            {/* Governance Tab */}
            <TabsContent value="governance" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Owner</Label>
                  <Input
                    value={formData.owner}
                    onChange={(e) => setFormData(prev => ({ ...prev, owner: e.target.value }))}
                    placeholder="e.g., john.doe@company.com"
                    className="bg-[#030712] border-[#1E293B]"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Reviewer</Label>
                  <Input
                    value={formData.reviewer}
                    onChange={(e) => setFormData(prev => ({ ...prev, reviewer: e.target.value }))}
                    placeholder="e.g., jane.doe@company.com"
                    className="bg-[#030712] border-[#1E293B]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Effective From</Label>
                  <Input
                    type="date"
                    value={formData.effective_from}
                    onChange={(e) => setFormData(prev => ({ ...prev, effective_from: e.target.value }))}
                    className="bg-[#030712] border-[#1E293B]"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Effective To</Label>
                  <Input
                    type="date"
                    value={formData.effective_to}
                    onChange={(e) => setFormData(prev => ({ ...prev, effective_to: e.target.value }))}
                    className="bg-[#030712] border-[#1E293B]"
                  />
                </div>
              </div>

              <div className="p-4 bg-[#030712] rounded-md border border-[#1E293B]">
                <Label className="text-[#64748B] text-xs">Approval Workflow</Label>
                <p className="text-xs text-[#64748B] mt-2">
                  Coming soon: Configure approval workflows for production concepts.
                </p>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveConcept} className="bg-[#0EA5E9] hover:bg-[#0284C7]">
              {editMode ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Version History Dialog with Diff */}
      <Dialog open={showVersionHistory} onOpenChange={(open) => { setShowVersionHistory(open); if (!open) { setSelectedVersionForDiff(null); setDiffData(null); } }}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden bg-[#0F172A] border-[#1E293B]">
          <DialogHeader>
            <DialogTitle className="text-[#F1F5F9] flex items-center gap-2">
              <History className="w-5 h-5" />
              Version History - {selectedConcept?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="flex gap-4 h-[60vh]">
            {/* Version List */}
            <div className="w-64 shrink-0 overflow-y-auto pr-2 border-r border-[#1E293B]">
              <div className="space-y-2">
                {versions.length === 0 ? (
                  <p className="text-center text-[#64748B] py-8">No version history</p>
                ) : (
                  versions.map((version, index) => (
                    <div 
                      key={version.id} 
                      className={cn(
                        "p-3 rounded-md border cursor-pointer transition-colors",
                        selectedVersionForDiff === version.version 
                          ? "bg-[#0EA5E9]/20 border-[#0EA5E9]/50" 
                          : index === 0 
                            ? "bg-[#0EA5E9]/10 border-[#0EA5E9]/30 hover:bg-[#0EA5E9]/15" 
                            : "bg-[#030712] border-[#1E293B] hover:bg-[#1E293B]/50"
                      )}
                      onClick={() => selectedConcept && loadDiff(selectedConcept.id, version.version)}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <Badge variant="outline" className="text-xs">v{version.version}</Badge>
                        {index === 0 && <Badge className="bg-[#0EA5E9]/20 text-[#0EA5E9] text-[10px]">Current</Badge>}
                      </div>
                      <p className="text-sm text-[#F1F5F9] truncate">{version.name}</p>
                      <p className="text-[10px] text-[#64748B] mt-1">
                        {new Date(version.created_at).toLocaleString()}
                      </p>
                      {index > 0 && (
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="mt-2 h-7 text-xs w-full"
                          onClick={async (e) => {
                            e.stopPropagation();
                            await restoreConceptVersion(selectedConcept.id, version.id);
                            toast.success('Version restored');
                            loadConcepts();
                            loadVersions(selectedConcept.id);
                          }}
                        >
                          <RotateCcw className="w-3 h-3 mr-1" /> Restore
                        </Button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
            
            {/* Diff View */}
            <div className="flex-1 overflow-y-auto">
              {loadingDiff ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-6 h-6 animate-spin text-[#0EA5E9]" />
                </div>
              ) : diffData ? (
                <div className="space-y-4">
                  {/* Name Diff */}
                  {diffData.name?.old !== diffData.name?.new && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-[#F1F5F9] flex items-center gap-2">
                        <Tag className="w-4 h-4" /> Name Change
                      </h4>
                      <div className="rounded-md overflow-hidden border border-[#1E293B]">
                        <ReactDiffViewer
                          oldValue={diffData.name?.old || ''}
                          newValue={diffData.name?.new || ''}
                          splitView={false}
                          useDarkTheme={true}
                          compareMethod={DiffMethod.WORDS}
                          styles={{
                            variables: {
                              dark: {
                                diffViewerBackground: '#030712',
                                addedBackground: '#10B98133',
                                removedBackground: '#EF444433',
                                addedColor: '#10B981',
                                removedColor: '#EF4444',
                                wordAddedBackground: '#10B98155',
                                wordRemovedBackground: '#EF444455',
                              }
                            },
                            contentText: { fontSize: '13px', fontFamily: 'monospace' }
                          }}
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Description Diff */}
                  {diffData.description?.old !== diffData.description?.new && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-[#F1F5F9] flex items-center gap-2">
                        <FileText className="w-4 h-4" /> Description Change
                      </h4>
                      <div className="rounded-md overflow-hidden border border-[#1E293B]">
                        <ReactDiffViewer
                          oldValue={diffData.description?.old || ''}
                          newValue={diffData.description?.new || ''}
                          splitView={false}
                          useDarkTheme={true}
                          compareMethod={DiffMethod.WORDS}
                          styles={{
                            variables: {
                              dark: {
                                diffViewerBackground: '#030712',
                                addedBackground: '#10B98133',
                                removedBackground: '#EF444433',
                                addedColor: '#10B981',
                                removedColor: '#EF4444',
                                wordAddedBackground: '#10B98155',
                                wordRemovedBackground: '#EF444455',
                              }
                            },
                            contentText: { fontSize: '13px' }
                          }}
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Definition Diff */}
                  {diffData.definition?.old !== diffData.definition?.new && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-[#F1F5F9] flex items-center gap-2">
                        <BookOpen className="w-4 h-4" /> Definition Change
                      </h4>
                      <div className="rounded-md overflow-hidden border border-[#1E293B]">
                        <ReactDiffViewer
                          oldValue={diffData.definition?.old || ''}
                          newValue={diffData.definition?.new || ''}
                          splitView={false}
                          useDarkTheme={true}
                          compareMethod={DiffMethod.LINES}
                          styles={{
                            variables: {
                              dark: {
                                diffViewerBackground: '#030712',
                                addedBackground: '#10B98133',
                                removedBackground: '#EF444433',
                                addedColor: '#10B981',
                                removedColor: '#EF4444',
                                wordAddedBackground: '#10B98155',
                                wordRemovedBackground: '#EF444455',
                              }
                            },
                            contentText: { fontSize: '12px', fontFamily: 'monospace' }
                          }}
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* No changes */}
                  {diffData.name?.old === diffData.name?.new && 
                   diffData.description?.old === diffData.description?.new && 
                   diffData.definition?.old === diffData.definition?.new && (
                    <div className="flex items-center justify-center h-32 text-[#64748B]">
                      <p>{diffData.has_previous ? 'No changes in this version' : 'Initial version - no previous version to compare'}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-[#64748B]">
                  <div className="text-center">
                    <History className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Select a version to view changes</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Similar Search Dialog */}
      <Dialog open={showSimilarSearch} onOpenChange={setShowSimilarSearch}>
        <DialogContent className="max-w-3xl bg-[#0F172A] border-[#1E293B]">
          <DialogHeader>
            <DialogTitle className="text-[#F1F5F9] flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#0EA5E9]" />
              Find Similar Concepts
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="flex gap-2">
              <Input
                value={similarQuery}
                onChange={(e) => setSimilarQuery(e.target.value)}
                placeholder="Search by name, alias, or description..."
                className="bg-[#030712] border-[#1E293B]"
                onKeyPress={(e) => e.key === 'Enter' && handleSearchSimilar()}
              />
              <Button onClick={handleSearchSimilar} disabled={searchingSimilar} className="bg-[#0EA5E9]">
                {searchingSimilar ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </div>

            {similarResults.length > 0 && (
              <div className="space-y-2">
                {similarResults.map(result => (
                  <div key={result.id} className="p-3 bg-[#030712] rounded-md border border-[#1E293B]">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-[#F1F5F9]">{result.name}</span>
                        <Badge variant="outline" className="text-[10px]">{result.concept_type}</Badge>
                        {result.matched_alias && (
                          <Badge className="bg-yellow-500/20 text-yellow-400 text-[10px]">
                            Alias: {result.matched_alias}
                          </Badge>
                        )}
                      </div>
                      <span className="text-xs text-[#0EA5E9] font-mono">
                        {(result.similarity_score * 100).toFixed(0)}% match
                      </span>
                    </div>
                    <p className="text-xs text-[#64748B] mt-1">{result.description}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Import/Export Dialog */}
      <Dialog open={showImportExport} onOpenChange={setShowImportExport}>
        <DialogContent className="max-w-2xl bg-[#0F172A] border-[#1E293B]">
          <DialogHeader>
            <DialogTitle className="text-[#F1F5F9] flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Import / Export Concepts
            </DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="import" className="py-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="import">Import</TabsTrigger>
              <TabsTrigger value="export">Export</TabsTrigger>
            </TabsList>
            <TabsContent value="import" className="mt-4">
              <div className="p-4 border-2 border-dashed border-[#1E293B] rounded-md text-center">
                <Upload className="w-8 h-8 mx-auto mb-2 text-[#64748B]" />
                <p className="text-sm text-[#64748B]">Drag & drop JSON/YAML file</p>
                <Button variant="outline" className="mt-2">Browse Files</Button>
              </div>
            </TabsContent>
            <TabsContent value="export" className="mt-4 space-y-4">
              <Button className="w-full bg-[#0EA5E9]" onClick={async () => {
                const res = await bulkExportConcepts({});
                const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'concepts_export.json';
                a.click();
                toast.success(`Exported ${res.data.total} concepts`);
              }}>
                <Download className="w-4 h-4 mr-2" /> Export All Concepts
              </Button>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Universal Instructions Dialog */}
      <Dialog open={showUniversalInstructions} onOpenChange={setShowUniversalInstructions}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden bg-[#0F172A] border-[#1E293B]">
          <DialogHeader>
            <DialogTitle className="text-[#F1F5F9] flex items-center gap-2">
              <ScrollText className="w-5 h-5 text-[#0EA5E9]" />
              Universal Instructions
              {universalInstructionVersion > 0 && (
                <Badge variant="outline" className="text-xs ml-2">v{universalInstructionVersion}</Badge>
              )}
            </DialogTitle>
            <DialogDescription className="text-[#64748B]">
              Define global instructions and prompt templates that apply across all concepts. Supports markdown formatting.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex gap-4 h-[55vh]">
            {/* Editor */}
            <div className="flex-1 flex flex-col">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm text-[#94A3B8]">Instruction Content</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { loadInstructionVersions(); setShowInstructionHistory(true); }}
                  className="text-[#64748B]"
                >
                  <History className="w-4 h-4 mr-1" />
                  History
                </Button>
              </div>
              <div className="flex-1 border border-[#1E293B] rounded-md overflow-hidden">
                <CodeMirror
                  value={universalInstruction}
                  onChange={(value) => setUniversalInstruction(value)}
                  theme={oneDark}
                  extensions={[markdown()]}
                  className="h-full text-sm"
                  placeholder={`# Universal Instructions\n\n## Role Definition\nYou are an expert data analyst assistant...\n\n## Output Format\n- Always use proper SQL syntax\n- Include comments explaining complex logic`}
                />
              </div>
              
              {/* Formatting hints */}
              <div className="mt-2 p-2 bg-[#030712] rounded border border-[#1E293B]">
                <p className="text-[10px] text-[#64748B]">
                  <strong>Formatting:</strong> Use <code className="text-[#0EA5E9]"># Heading</code>, <code className="text-[#0EA5E9]">**bold**</code>, <code className="text-[#0EA5E9]">`code`</code>, <code className="text-[#0EA5E9]">- lists</code>
                </p>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUniversalInstructions(false)}>Cancel</Button>
            <Button onClick={saveUniversalInstruction} disabled={savingInstruction} className="bg-[#0EA5E9]">
              {savingInstruction ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
              Save Instructions
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Universal Instructions Version History Dialog */}
      <Dialog open={showInstructionHistory} onOpenChange={(open) => { setShowInstructionHistory(open); if (!open) { setSelectedInstructionVersion(null); setInstructionDiffData(null); } }}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden bg-[#0F172A] border-[#1E293B]">
          <DialogHeader>
            <DialogTitle className="text-[#F1F5F9] flex items-center gap-2">
              <History className="w-5 h-5" />
              Universal Instructions - Version History
            </DialogTitle>
          </DialogHeader>
          <div className="flex gap-4 h-[60vh]">
            {/* Version List */}
            <div className="w-64 shrink-0 overflow-y-auto pr-2 border-r border-[#1E293B]">
              <div className="space-y-2">
                {universalInstructionVersions.length === 0 ? (
                  <p className="text-center text-[#64748B] py-8">No version history</p>
                ) : (
                  universalInstructionVersions.map((version, index) => (
                    <div 
                      key={version.id} 
                      className={cn(
                        "p-3 rounded-md border cursor-pointer transition-colors",
                        selectedInstructionVersion === version.version 
                          ? "bg-[#0EA5E9]/20 border-[#0EA5E9]/50" 
                          : index === 0 
                            ? "bg-[#0EA5E9]/10 border-[#0EA5E9]/30 hover:bg-[#0EA5E9]/15" 
                            : "bg-[#030712] border-[#1E293B] hover:bg-[#1E293B]/50"
                      )}
                      onClick={() => loadInstructionDiff(version.version)}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <Badge variant="outline" className="text-xs">v{version.version}</Badge>
                        {index === 0 && <Badge className="bg-[#0EA5E9]/20 text-[#0EA5E9] text-[10px]">Current</Badge>}
                      </div>
                      <p className="text-[10px] text-[#64748B] mt-1">
                        {new Date(version.created_at).toLocaleString()}
                      </p>
                      <p className="text-[10px] text-[#94A3B8] mt-1 truncate">{version.change_note}</p>
                      {index > 0 && (
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="mt-2 h-7 text-xs w-full"
                          onClick={(e) => {
                            e.stopPropagation();
                            restoreInstructionVersion(version.version);
                          }}
                        >
                          <RotateCcw className="w-3 h-3 mr-1" /> Restore
                        </Button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
            
            {/* Diff View */}
            <div className="flex-1 overflow-y-auto">
              {loadingInstructionDiff ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-6 h-6 animate-spin text-[#0EA5E9]" />
                </div>
              ) : instructionDiffData ? (
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-[#F1F5F9] flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Content Changes
                  </h4>
                  <div className="rounded-md overflow-hidden border border-[#1E293B]">
                    <ReactDiffViewer
                      oldValue={instructionDiffData.content?.old || ''}
                      newValue={instructionDiffData.content?.new || ''}
                      splitView={false}
                      useDarkTheme={true}
                      compareMethod={DiffMethod.LINES}
                      styles={{
                        variables: {
                          dark: {
                            diffViewerBackground: '#030712',
                            addedBackground: '#10B98133',
                            removedBackground: '#EF444433',
                            addedColor: '#10B981',
                            removedColor: '#EF4444',
                            wordAddedBackground: '#10B98155',
                            wordRemovedBackground: '#EF444455',
                          }
                        },
                        contentText: { fontSize: '12px', fontFamily: 'monospace' }
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-[#64748B]">
                  <div className="text-center">
                    <History className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Select a version to view changes</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Concept Detail View Component
function ConceptDetailView({ concept, onEdit, onDelete, onViewHistory, onDuplicate }) {
  const [activeTab, setActiveTab] = useState('overview');
  const TypeIcon = typeConfig[concept.concept_type]?.icon || Calculator;
  const StatusIcon = statusIcons[concept.status] || Clock;

  return (
    <div className="h-full flex flex-col bg-[#030712]">
      {/* Header */}
      <div className="p-4 border-b border-[#1E293B] bg-[#0F172A]">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <div className={cn("p-1.5 rounded", typeConfig[concept.concept_type]?.bg)}>
                <TypeIcon className={cn("w-4 h-4", typeConfig[concept.concept_type]?.color)} />
              </div>
              <h3 className="text-lg text-[#F1F5F9] font-medium">{concept.name}</h3>
              <Badge variant="outline" className={cn("text-xs", statusColors[concept.status])}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {concept.status}
              </Badge>
              <Badge variant="outline" className="text-xs text-[#64748B]">v{concept.version}</Badge>
            </div>
            <p className="text-sm text-[#94A3B8]">{concept.description}</p>
            <div className="flex flex-wrap gap-2 mt-2">
              {concept.team && <Badge variant="outline" className="text-[10px] text-[#64748B]">{concept.team}</Badge>}
              {concept.tags?.slice(0, 3).map(tag => (
                <Badge key={tag} variant="outline" className="text-[10px] text-[#64748B]">
                  <Tag className="w-3 h-3 mr-1" />{tag}
                </Badge>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={onViewHistory} title="View History">
              <History className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onDuplicate} title="Duplicate">
              <Copy className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onEdit} title="Edit">
              <Edit className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onDelete} className="text-red-400" title="Delete">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
        <TabsList className="w-full justify-start rounded-none border-b border-[#1E293B] bg-transparent h-10 p-0">
          <TabsTrigger value="overview" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0EA5E9]">
            Overview
          </TabsTrigger>
          <TabsTrigger value="calculation" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0EA5E9]">
            Calculation
          </TabsTrigger>
          <TabsTrigger value="metadata" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0EA5E9]">
            Metadata
          </TabsTrigger>
          <TabsTrigger value="sources" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0EA5E9]">
            Sources
          </TabsTrigger>
          <TabsTrigger value="governance" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0EA5E9]">
            Governance
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="flex-1 overflow-auto p-4 m-0 space-y-4">
          {concept.definition && (
            <div className="p-4 bg-[#0F172A] rounded-md border border-[#1E293B]">
              <Label className="text-xs text-[#64748B] flex items-center gap-1 mb-3">
                <FileText className="w-3 h-3" /> Definition
              </Label>
              <div className="prose prose-sm prose-invert max-w-none definition-content">
                {concept.definition.split('\n').map((line, i) => {
                  // Render markdown-style formatting
                  if (line.startsWith('## ')) {
                    return <h3 key={i} className="text-[#0EA5E9] text-sm font-medium mt-4 mb-2">{line.slice(3)}</h3>;
                  } else if (line.startsWith('### ')) {
                    return <h4 key={i} className="text-[#94A3B8] text-sm font-medium mt-3 mb-1">{line.slice(4)}</h4>;
                  } else if (line.startsWith('- ')) {
                    return <li key={i} className="text-sm text-[#F1F5F9] ml-4 list-disc">{line.slice(2)}</li>;
                  } else if (line.startsWith('**') && line.endsWith('**')) {
                    return <p key={i} className="text-sm text-[#F1F5F9] font-semibold">{line.slice(2, -2)}</p>;
                  } else if (line.trim() === '') {
                    return <div key={i} className="h-2" />;
                  } else {
                    // Handle inline bold
                    const parts = line.split(/(\*\*[^*]+\*\*)/g);
                    return (
                      <p key={i} className="text-sm text-[#F1F5F9]">
                        {parts.map((part, j) => {
                          if (part.startsWith('**') && part.endsWith('**')) {
                            return <strong key={j} className="font-semibold text-[#0EA5E9]">{part.slice(2, -2)}</strong>;
                          }
                          return part;
                        })}
                      </p>
                    );
                  }
                })}
              </div>
            </div>
          )}
          {concept.aliases?.length > 0 && (
            <div className="p-3 bg-[#0F172A] rounded-md border border-[#1E293B]">
              <Label className="text-xs text-[#64748B]">Also Known As</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {concept.aliases.map((alias, i) => (
                  <Badge key={i} variant="outline">{alias}</Badge>
                ))}
              </div>
            </div>
          )}
          {concept.known_limitations && (
            <div className="p-3 bg-yellow-500/5 rounded-md border border-yellow-500/20">
              <Label className="text-xs text-yellow-400 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Known Limitations
              </Label>
              <p className="text-sm text-[#94A3B8] mt-1">{concept.known_limitations}</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="calculation" className="flex-1 overflow-auto p-4 m-0 space-y-4">
          {concept.calculation_steps?.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs text-[#64748B]">Calculation Steps</Label>
              {concept.calculation_steps.map((step, i) => (
                <div key={i} className="p-3 bg-[#0F172A] rounded-md border border-[#1E293B]">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="outline">Step {step.step_number || i + 1}</Badge>
                  </div>
                  <p className="text-sm text-[#F1F5F9]">{step.description}</p>
                  {step.formula && (
                    <code className="block mt-2 text-xs text-[#0EA5E9] bg-[#030712] p-2 rounded font-mono">
                      {step.formula}
                    </code>
                  )}
                </div>
              ))}
            </div>
          )}
          {concept.computation_methods?.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs text-[#64748B]">SQL Templates</Label>
              {concept.computation_methods.map((method, i) => (
                <div key={i} className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{method.dialect}</Badge>
                    {method.is_default && <Badge className="bg-[#0EA5E9]/20 text-[#0EA5E9] text-[10px]">Default</Badge>}
                  </div>
                  <div className="rounded-md overflow-hidden border border-[#1E293B]">
                    <CodeMirror
                      value={method.sql_template}
                      height="auto"
                      minHeight="80px"
                      maxHeight="200px"
                      theme={oneDark}
                      extensions={[sql()]}
                      editable={false}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Metadata Tab */}
        <TabsContent value="metadata" className="flex-1 overflow-hidden p-0 m-0">
          <MetadataTabContent conceptId={concept.id} />
        </TabsContent>

        <TabsContent value="sources" className="flex-1 overflow-auto p-4 m-0 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-[#0F172A] rounded-md border border-[#1E293B]">
              <Label className="text-xs text-[#64748B]">Base Tables</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {concept.base_tables?.map((table, i) => (
                  <Badge key={i} variant="outline" className="font-mono">
                    <Database className="w-3 h-3 mr-1" /> {table}
                  </Badge>
                ))}
                {(!concept.base_tables || concept.base_tables.length === 0) && (
                  <span className="text-xs text-[#64748B]">None specified</span>
                )}
              </div>
            </div>
            <div className="p-3 bg-[#0F172A] rounded-md border border-[#1E293B]">
              <Label className="text-xs text-[#64748B]">Base Columns</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {concept.base_columns?.slice(0, 8).map((col, i) => (
                  <Badge key={i} variant="outline" className="font-mono text-xs">{col}</Badge>
                ))}
                {concept.base_columns?.length > 8 && (
                  <Badge variant="outline">+{concept.base_columns.length - 8}</Badge>
                )}
                {(!concept.base_columns || concept.base_columns.length === 0) && (
                  <span className="text-xs text-[#64748B]">None specified</span>
                )}
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="governance" className="flex-1 overflow-auto p-4 m-0 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-[#0F172A] rounded-md border border-[#1E293B]">
              <Label className="text-xs text-[#64748B]">Owner</Label>
              <p className="text-sm text-[#F1F5F9] mt-1">{concept.owner || 'Not assigned'}</p>
            </div>
            <div className="p-3 bg-[#0F172A] rounded-md border border-[#1E293B]">
              <Label className="text-xs text-[#64748B]">Reviewer</Label>
              <p className="text-sm text-[#F1F5F9] mt-1">{concept.reviewer || 'Not assigned'}</p>
            </div>
            <div className="p-3 bg-[#0F172A] rounded-md border border-[#1E293B]">
              <Label className="text-xs text-[#64748B]">Effective From</Label>
              <p className="text-sm text-[#F1F5F9] mt-1">{concept.effective_from || 'Not set'}</p>
            </div>
            <div className="p-3 bg-[#0F172A] rounded-md border border-[#1E293B]">
              <Label className="text-xs text-[#64748B]">Effective To</Label>
              <p className="text-sm text-[#F1F5F9] mt-1">{concept.effective_to || 'Not set'}</p>
            </div>
          </div>
          <div className="p-3 bg-[#0F172A] rounded-md border border-[#1E293B]">
            <Label className="text-xs text-[#64748B]">Data Freshness SLA</Label>
            <p className="text-sm text-[#F1F5F9] mt-1">{concept.data_freshness_sla || 'Not specified'}</p>
          </div>
          {concept.audit_trail?.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs text-[#64748B]">Audit Trail (Recent)</Label>
              {concept.audit_trail.slice(-5).reverse().map((entry, i) => (
                <div key={i} className="p-2 bg-[#030712] rounded border border-[#1E293B] text-xs">
                  <span className="text-[#0EA5E9]">{entry.action}</span>
                  <span className="text-[#64748B]"> by {entry.user} at {new Date(entry.timestamp).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============ Metadata Tab Content Component ============
function MetadataTabContent({ conceptId }) {
  const [metadata, setMetadata] = useState({ columns: [], links: [], version: 1 });
  const [loading, setLoading] = useState(true);
  const [activeSubTab, setActiveSubTab] = useState('columns');
  const [editingCell, setEditingCell] = useState(null);
  const [showVersions, setShowVersions] = useState(false);
  const [versions, setVersions] = useState({ current: null, history: [] });
  const [showAddColumn, setShowAddColumn] = useState(false);
  const [showAddLink, setShowAddLink] = useState(false);
  const [showGlossaryRegen, setShowGlossaryRegen] = useState(false);
  const [glossaryInstructions, setGlossaryInstructions] = useState('');
  const [glossaryTarget, setGlossaryTarget] = useState('column');
  const [regenerating, setRegenerating] = useState(false);
  const [newColumn, setNewColumn] = useState({ source_table: '', source_column: '' });
  const [newLink, setNewLink] = useState({ parent_table_name: '', parent_column_name: '', child_table_name: '', child_column_name: '' });

  // Load metadata
  const loadMetadata = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getConceptMetadata(conceptId);
      setMetadata(res.data);
    } catch (err) {
      toast.error('Failed to load metadata');
    } finally {
      setLoading(false);
    }
  }, [conceptId]);

  useEffect(() => {
    loadMetadata();
  }, [loadMetadata]);

  // Load versions
  const loadVersions = async () => {
    try {
      const res = await getMetadataVersions(conceptId);
      setVersions(res.data);
    } catch (err) {
      toast.error('Failed to load versions');
    }
  };

  // Handle toggle change
  const handleToggleChange = async (columnId, field, value) => {
    try {
      await updateColumnMetadata(conceptId, columnId, { [field]: value });
      loadMetadata();
      toast.success('Updated');
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  // Handle text field save
  const handleFieldSave = async (columnId, field, value) => {
    try {
      await updateColumnMetadata(conceptId, columnId, { [field]: value });
      setEditingCell(null);
      loadMetadata();
      toast.success('Saved');
    } catch (err) {
      toast.error('Failed to save');
    }
  };

  // Add new column
  const handleAddColumn = async () => {
    if (!newColumn.source_table || !newColumn.source_column) {
      toast.error('Table and column names are required');
      return;
    }
    try {
      await addColumnMetadata(conceptId, newColumn);
      setShowAddColumn(false);
      setNewColumn({ source_table: '', source_column: '' });
      loadMetadata();
      toast.success('Column added');
    } catch (err) {
      toast.error('Failed to add column');
    }
  };

  // Delete column
  const handleDeleteColumn = async (columnId) => {
    if (!window.confirm('Delete this column metadata?')) return;
    try {
      await deleteColumnMetadata(conceptId, columnId);
      loadMetadata();
      toast.success('Column deleted');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  // Add new link
  const handleAddLink = async () => {
    if (!newLink.parent_table_name || !newLink.parent_column_name || !newLink.child_table_name || !newLink.child_column_name) {
      toast.error('All fields are required');
      return;
    }
    try {
      await addLink(conceptId, newLink);
      setShowAddLink(false);
      setNewLink({ parent_table_name: '', parent_column_name: '', child_table_name: '', child_column_name: '' });
      loadMetadata();
      toast.success('Link added');
    } catch (err) {
      toast.error('Failed to add link');
    }
  };

  // Delete link
  const handleDeleteLink = async (linkId) => {
    if (!window.confirm('Delete this link?')) return;
    try {
      await deleteLink(conceptId, linkId);
      loadMetadata();
      toast.success('Link deleted');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  // Restore version
  const handleRestoreVersion = async (versionId) => {
    if (!window.confirm('Restore to this version? Current changes will be saved in history.')) return;
    try {
      await restoreMetadataVersion(conceptId, versionId);
      loadMetadata();
      loadVersions();
      toast.success('Version restored');
    } catch (err) {
      toast.error('Failed to restore');
    }
  };

  // Delete version
  const handleDeleteVersion = async (versionId) => {
    if (!window.confirm('Delete this version from history?')) return;
    try {
      await deleteMetadataVersion(conceptId, versionId);
      loadVersions();
      toast.success('Version deleted');
    } catch (err) {
      toast.error('Failed to delete version');
    }
  };

  // Regenerate glossary
  const handleRegenerateGlossary = async () => {
    setRegenerating(true);
    try {
      const res = await regenerateGlossary(conceptId, {
        special_instructions: glossaryInstructions,
        target: glossaryTarget
      });
      if (res.data.success) {
        toast.success(res.data.message);
        loadMetadata();
      } else {
        toast.error(res.data.message || 'Failed to regenerate');
      }
      setShowGlossaryRegen(false);
    } catch (err) {
      toast.error('Failed to regenerate glossary');
    } finally {
      setRegenerating(false);
    }
  };

  // Editable cell component
  const EditableCell = ({ columnId, field, value, type = 'text' }) => {
    const cellKey = `${columnId}-${field}`;
    const isEditing = editingCell === cellKey;
    const [localValue, setLocalValue] = useState(value || '');

    if (type === 'tags') {
      return (
        <div className="flex flex-wrap gap-1">
          {(value || []).map((tag, i) => (
            <Badge key={i} variant="outline" className="text-[10px]">{tag}</Badge>
          ))}
        </div>
      );
    }

    if (isEditing) {
      return (
        <div className="flex items-center gap-1">
          <Input
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            className="h-6 text-xs bg-[#030712] border-[#1E293B] px-1"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleFieldSave(columnId, field, localValue);
              if (e.key === 'Escape') setEditingCell(null);
            }}
          />
          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => handleFieldSave(columnId, field, localValue)}>
            <Save className="w-3 h-3 text-green-400" />
          </Button>
        </div>
      );
    }

    return (
      <span 
        className="cursor-pointer hover:text-[#0EA5E9] text-xs"
        onClick={() => { setEditingCell(cellKey); setLocalValue(value || ''); }}
      >
        {value || <span className="text-[#64748B] italic">Click to edit</span>}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-6 h-6 animate-spin text-[#0EA5E9]" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-3 border-b border-[#1E293B] flex items-center justify-between bg-[#0F172A]">
        <div className="flex items-center gap-2">
          <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
            <TabsList className="bg-[#030712]">
              <TabsTrigger value="columns" className="text-xs data-[state=active]:bg-[#0EA5E9]/20">
                <Columns className="w-3 h-3 mr-1" />
                Column Metadata
              </TabsTrigger>
              <TabsTrigger value="links" className="text-xs data-[state=active]:bg-[#0EA5E9]/20">
                <LinkIcon className="w-3 h-3 mr-1" />
                Link Identification
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <Badge variant="outline" className="text-[10px]">v{metadata.version}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => { loadVersions(); setShowVersions(true); }}>
            <History className="w-4 h-4 mr-1" />
            Versions
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setShowGlossaryRegen(true)}>
            <RefreshCw className="w-4 h-4 mr-1" />
            Regenerate Glossary
          </Button>
          {activeSubTab === 'columns' && (
            <Button size="sm" onClick={() => setShowAddColumn(true)} className="bg-[#0EA5E9] hover:bg-[#0284C7]">
              <Plus className="w-4 h-4 mr-1" />
              Add Column
            </Button>
          )}
          {activeSubTab === 'links' && (
            <Button size="sm" onClick={() => setShowAddLink(true)} className="bg-[#0EA5E9] hover:bg-[#0284C7]">
              <Plus className="w-4 h-4 mr-1" />
              Add Link
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <ScrollArea className="flex-1">
        {activeSubTab === 'columns' && (
          <div className="p-4">
            {metadata.columns.length === 0 ? (
              <div className="text-center py-12 text-[#64748B]">
                <Table className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No column metadata defined</p>
                <Button size="sm" className="mt-4" onClick={() => setShowAddColumn(true)}>
                  <Plus className="w-4 h-4 mr-1" /> Add First Column
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {metadata.columns.map((col) => (
                  <div key={col.id} className="p-4 bg-[#0F172A] rounded-lg border border-[#1E293B]">
                    {/* Column Header */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <Database className="w-4 h-4 text-[#0EA5E9]" />
                        <span className="font-mono text-sm text-[#F1F5F9]">{col.source_table}.{col.source_column}</span>
                        {col.is_primary_key && <Badge className="bg-yellow-500/20 text-yellow-400 text-[10px]"><Key className="w-3 h-3 mr-1" />PK</Badge>}
                        {col.is_foreign_key && <Badge className="bg-purple-500/20 text-purple-400 text-[10px]"><Key className="w-3 h-3 mr-1" />FK</Badge>}
                        {col.is_pii && <Badge className="bg-red-500/20 text-red-400 text-[10px]">PII</Badge>}
                      </div>
                      <Button size="sm" variant="ghost" className="text-red-400" onClick={() => handleDeleteColumn(col.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* System-derived fields (non-editable) */}
                    <div className="grid grid-cols-5 gap-3 mb-4 p-3 bg-[#030712] rounded border border-[#1E293B]">
                      <div>
                        <Label className="text-[10px] text-[#64748B]">Row Count</Label>
                        <p className="text-xs text-[#94A3B8] font-mono">{col.row_count_system?.toLocaleString() ?? '-'}</p>
                      </div>
                      <div>
                        <Label className="text-[10px] text-[#64748B]">Unique Count</Label>
                        <p className="text-xs text-[#94A3B8] font-mono">{col.unique_count_system?.toLocaleString() ?? '-'}</p>
                      </div>
                      <div>
                        <Label className="text-[10px] text-[#64748B]">Uniqueness %</Label>
                        <p className="text-xs text-[#94A3B8] font-mono">{col.uniqueness_pct_system != null ? `${col.uniqueness_pct_system.toFixed(1)}%` : '-'}</p>
                      </div>
                      <div>
                        <Label className="text-[10px] text-[#64748B]">Completeness %</Label>
                        <p className="text-xs text-[#94A3B8] font-mono">{col.completeness_pct_system != null ? `${col.completeness_pct_system.toFixed(1)}%` : '-'}</p>
                      </div>
                      <div>
                        <Label className="text-[10px] text-[#64748B]">Sample Values</Label>
                        <p className="text-xs text-[#94A3B8] truncate">{col.sample_values?.slice(0, 3).join(', ') || '-'}</p>
                      </div>
                    </div>

                    {/* Editable fields */}
                    <div className="grid grid-cols-4 gap-4 mb-4">
                      <div>
                        <Label className="text-[10px] text-[#64748B]">Table Alias</Label>
                        <EditableCell columnId={col.id} field="table_alias" value={col.table_alias} />
                      </div>
                      <div>
                        <Label className="text-[10px] text-[#64748B]">Column Alias</Label>
                        <EditableCell columnId={col.id} field="column_alias" value={col.column_alias} />
                      </div>
                      <div>
                        <Label className="text-[10px] text-[#64748B]">Datatype L1</Label>
                        <EditableCell columnId={col.id} field="datatype_l1" value={col.datatype_l1} />
                      </div>
                      <div>
                        <Label className="text-[10px] text-[#64748B]">Datatype L2</Label>
                        <EditableCell columnId={col.id} field="datatype_l2" value={col.datatype_l2} />
                      </div>
                    </div>

                    <div className="grid grid-cols-4 gap-4 mb-4">
                      <div>
                        <Label className="text-[10px] text-[#64748B]">Table Type</Label>
                        <EditableCell columnId={col.id} field="table_type" value={col.table_type} />
                      </div>
                      <div>
                        <Label className="text-[10px] text-[#64748B]">Column Type</Label>
                        <EditableCell columnId={col.id} field="column_type" value={col.column_type} />
                      </div>
                      <div>
                        <Label className="text-[10px] text-[#64748B]">Column Tags</Label>
                        <EditableCell columnId={col.id} field="column_tags" value={col.column_tags} type="tags" />
                      </div>
                      <div>
                        <Label className="text-[10px] text-[#64748B]">Calculation Logic</Label>
                        <EditableCell columnId={col.id} field="calculation_logic" value={col.calculation_logic} />
                      </div>
                    </div>

                    {/* Glossaries */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <Label className="text-[10px] text-[#64748B]">Column Glossary</Label>
                        <EditableCell columnId={col.id} field="column_glossary" value={col.column_glossary} />
                      </div>
                      <div>
                        <Label className="text-[10px] text-[#64748B]">Table Glossary</Label>
                        <EditableCell columnId={col.id} field="table_glossary" value={col.table_glossary} />
                      </div>
                    </div>

                    {/* Toggle fields */}
                    <div className="grid grid-cols-6 gap-4 pt-3 border-t border-[#1E293B]">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={col.is_pii || false}
                          onCheckedChange={(v) => handleToggleChange(col.id, 'is_pii', v)}
                        />
                        <Label className="text-[10px]">PII</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={col.is_ignored || false}
                          onCheckedChange={(v) => handleToggleChange(col.id, 'is_ignored', v)}
                        />
                        <Label className="text-[10px]">Ignored</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={col.is_primary_key || false}
                          onCheckedChange={(v) => handleToggleChange(col.id, 'is_primary_key', v)}
                        />
                        <Label className="text-[10px]">Primary Key</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={col.is_foreign_key || false}
                          onCheckedChange={(v) => handleToggleChange(col.id, 'is_foreign_key', v)}
                        />
                        <Label className="text-[10px]">Foreign Key</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={col.row_shortlisting_enabled || false}
                          onCheckedChange={(v) => handleToggleChange(col.id, 'row_shortlisting_enabled', v)}
                        />
                        <Label className="text-[10px]">Row Shortlist</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={col.auto_correction_enabled || false}
                          onCheckedChange={(v) => handleToggleChange(col.id, 'auto_correction_enabled', v)}
                        />
                        <Label className="text-[10px]">Auto Correct</Label>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeSubTab === 'links' && (
          <div className="p-4">
            {metadata.links.length === 0 ? (
              <div className="text-center py-12 text-[#64748B]">
                <LinkIcon className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No link relationships defined</p>
                <Button size="sm" className="mt-4" onClick={() => setShowAddLink(true)}>
                  <Plus className="w-4 h-4 mr-1" /> Add First Link
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {metadata.links.map((link) => (
                  <div key={link.id} className="p-4 bg-[#0F172A] rounded-lg border border-[#1E293B]">
                    {/* Link Header - Editable */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <div className="p-2 bg-[#030712] rounded border border-[#1E293B]">
                          <span className="font-mono text-sm text-[#F1F5F9]">{link.parent_table_name}.{link.parent_column_name}</span>
                        </div>
                        <ArrowRight className="w-4 h-4 text-[#0EA5E9]" />
                        <div className="p-2 bg-[#030712] rounded border border-[#1E293B]">
                          <span className="font-mono text-sm text-[#F1F5F9]">{link.child_table_name}.{link.child_column_name}</span>
                        </div>
                      </div>
                      <Button size="sm" variant="ghost" className="text-red-400" onClick={() => handleDeleteLink(link.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* System-derived fields (non-editable) */}
                    <div className="grid grid-cols-5 gap-3 p-3 bg-[#030712] rounded border border-[#1E293B]">
                      <div>
                        <Label className="text-[10px] text-[#64748B]">Mapped Unique Values</Label>
                        <p className="text-xs text-[#94A3B8] font-mono">{link.mapped_unique_value_count?.toLocaleString() ?? '-'}</p>
                      </div>
                      <div>
                        <Label className="text-[10px] text-[#64748B]">Parent Row Count</Label>
                        <p className="text-xs text-[#94A3B8] font-mono">{link.parent_row_count_mapped?.toLocaleString() ?? '-'}</p>
                      </div>
                      <div>
                        <Label className="text-[10px] text-[#64748B]">Parent Unique Values</Label>
                        <p className="text-xs text-[#94A3B8] font-mono">{link.parent_unique_value_count_mapped?.toLocaleString() ?? '-'}</p>
                      </div>
                      <div>
                        <Label className="text-[10px] text-[#64748B]">Child Row Count</Label>
                        <p className="text-xs text-[#94A3B8] font-mono">{link.child_row_count_mapped?.toLocaleString() ?? '-'}</p>
                      </div>
                      <div>
                        <Label className="text-[10px] text-[#64748B]">Child Unique Values</Label>
                        <p className="text-xs text-[#94A3B8] font-mono">{link.child_unique_value_count_mapped?.toLocaleString() ?? '-'}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </ScrollArea>

      {/* Add Column Dialog */}
      <Dialog open={showAddColumn} onOpenChange={setShowAddColumn}>
        <DialogContent className="bg-[#0F172A] border-[#1E293B]">
          <DialogHeader>
            <DialogTitle className="text-[#F1F5F9]">Add Column Metadata</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Source Table *</Label>
              <Input
                value={newColumn.source_table}
                onChange={(e) => setNewColumn(prev => ({ ...prev, source_table: e.target.value }))}
                placeholder="e.g., customers"
                className="bg-[#030712] border-[#1E293B]"
              />
            </div>
            <div className="space-y-2">
              <Label>Source Column *</Label>
              <Input
                value={newColumn.source_column}
                onChange={(e) => setNewColumn(prev => ({ ...prev, source_column: e.target.value }))}
                placeholder="e.g., customer_id"
                className="bg-[#030712] border-[#1E293B]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddColumn(false)}>Cancel</Button>
            <Button onClick={handleAddColumn} className="bg-[#0EA5E9]">Add Column</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Link Dialog */}
      <Dialog open={showAddLink} onOpenChange={setShowAddLink}>
        <DialogContent className="bg-[#0F172A] border-[#1E293B]">
          <DialogHeader>
            <DialogTitle className="text-[#F1F5F9]">Add Link Relationship</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Parent Table *</Label>
                <Input
                  value={newLink.parent_table_name}
                  onChange={(e) => setNewLink(prev => ({ ...prev, parent_table_name: e.target.value }))}
                  placeholder="e.g., customers"
                  className="bg-[#030712] border-[#1E293B]"
                />
              </div>
              <div className="space-y-2">
                <Label>Parent Column *</Label>
                <Input
                  value={newLink.parent_column_name}
                  onChange={(e) => setNewLink(prev => ({ ...prev, parent_column_name: e.target.value }))}
                  placeholder="e.g., customer_id"
                  className="bg-[#030712] border-[#1E293B]"
                />
              </div>
            </div>
            <div className="flex justify-center">
              <ArrowRight className="w-6 h-6 text-[#64748B]" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Child Table *</Label>
                <Input
                  value={newLink.child_table_name}
                  onChange={(e) => setNewLink(prev => ({ ...prev, child_table_name: e.target.value }))}
                  placeholder="e.g., orders"
                  className="bg-[#030712] border-[#1E293B]"
                />
              </div>
              <div className="space-y-2">
                <Label>Child Column *</Label>
                <Input
                  value={newLink.child_column_name}
                  onChange={(e) => setNewLink(prev => ({ ...prev, child_column_name: e.target.value }))}
                  placeholder="e.g., customer_id"
                  className="bg-[#030712] border-[#1E293B]"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddLink(false)}>Cancel</Button>
            <Button onClick={handleAddLink} className="bg-[#0EA5E9]">Add Link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Version History Dialog */}
      <Dialog open={showVersions} onOpenChange={setShowVersions}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto bg-[#0F172A] border-[#1E293B]">
          <DialogHeader>
            <DialogTitle className="text-[#F1F5F9] flex items-center gap-2">
              <History className="w-5 h-5" />
              Metadata Version History
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-3">
            {/* Current Version */}
            {versions.current && (
              <div className="p-4 rounded-md border bg-[#0EA5E9]/10 border-[#0EA5E9]/30">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">v{versions.current.version}</Badge>
                    <Badge className="bg-[#0EA5E9]/20 text-[#0EA5E9] text-[10px]">Current</Badge>
                  </div>
                  <span className="text-[10px] text-[#64748B]">
                    {versions.current.updated_at && new Date(versions.current.updated_at).toLocaleString()}
                  </span>
                </div>
                <p className="text-sm text-[#94A3B8]">
                  {versions.current.columns?.length || 0} columns, {versions.current.links?.length || 0} links
                </p>
              </div>
            )}

            {/* Historical Versions */}
            {versions.history.length === 0 ? (
              <p className="text-center text-[#64748B] py-8">No version history</p>
            ) : (
              versions.history.map((version) => (
                <div key={version.id} className="p-4 rounded-md border bg-[#030712] border-[#1E293B]">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">v{version.version}</Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#64748B]">
                        {new Date(version.created_at).toLocaleString()}
                      </span>
                      <Button size="sm" variant="ghost" onClick={() => handleRestoreVersion(version.id)}>
                        <RotateCcw className="w-3 h-3 mr-1" /> Restore
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-400" onClick={() => handleDeleteVersion(version.id)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-sm text-[#94A3B8]">{version.change_summary}</p>
                  <p className="text-xs text-[#64748B] mt-1">
                    {version.columns?.length || 0} columns, {version.links?.length || 0} links
                  </p>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Regenerate Glossary Dialog */}
      <Dialog open={showGlossaryRegen} onOpenChange={setShowGlossaryRegen}>
        <DialogContent className="bg-[#0F172A] border-[#1E293B]">
          <DialogHeader>
            <DialogTitle className="text-[#F1F5F9] flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-[#0EA5E9]" />
              Regenerate Glossary
            </DialogTitle>
            <DialogDescription className="text-[#64748B]">
              Use AI to generate glossary descriptions for columns and tables
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Target</Label>
              <Select value={glossaryTarget} onValueChange={setGlossaryTarget}>
                <SelectTrigger className="bg-[#030712] border-[#1E293B]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="column">Column Glossary Only</SelectItem>
                  <SelectItem value="table">Table Glossary Only</SelectItem>
                  <SelectItem value="all">Both Column & Table</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Special Instructions (Optional)</Label>
              <Textarea
                value={glossaryInstructions}
                onChange={(e) => setGlossaryInstructions(e.target.value)}
                placeholder="E.g., Focus on business context, use simple language, include data quality notes..."
                className="bg-[#030712] border-[#1E293B] min-h-[100px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGlossaryRegen(false)}>Cancel</Button>
            <Button onClick={handleRegenerateGlossary} disabled={regenerating} className="bg-[#0EA5E9]">
              {regenerating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              Regenerate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
